// Christmas kids' bikes (table bikes_navidad_2025): names, ages, slugs and page texts shared by the
// React pages and scripts/inject-metadata.js (CommonJS so Node can require it).

const AGES = {
    12: '2 a 4 años (85 a 105 cm)',
    16: '4 a 6 años (100 a 120 cm)',
    20: '6 a 8 años (115 a 135 cm)',
    24: '8 a 12 años (130 a 150 cm)',
    26: '12 años en adelante (145 cm o más)',
    29: 'jóvenes y adultos (160 cm o más)'
};
const AGES_SHORT = { 12: '2 a 4 años', 16: '4 a 6 años', 20: '6 a 8 años', 24: '8 a 12 años', 26: '12 años en adelante', 29: 'jóvenes y adultos' };

const NAVIDAD = {
    path: '/navidad',
    breadcrumb: 'Bicicletas de Navidad',
    title: 'Bicicletas de Navidad para niños en Santo Domingo | PROOMTB & ROAD',
    description: (count, minPrice, aros) => `${count} bicicletas para niños en aros ${listOf(aros)} (Raleigh, Julen BMX y Carolina Princess) desde RD$${formatRD(minPrice)}, armadas y calibradas, con plan de apartado para Nochebuena y Reyes. Los Prados, Santo Domingo.`
};

// Wheel sizes the store sells this season: the shop (Shop.jsx), the home carousel, the pages,
// the sitemap, the Google feed and the profile products all follow this list.
const PROMO_AROS = ['12', '16', '20'];

function promoted(row) {
    return PROMO_AROS.includes(String(row.categoria));
}

// ['12', '16', '20'] -> '12, 16 y 20'
function listOf(items) {
    const list = [...items];
    const last = list.pop();
    return list.length ? `${list.join(', ')} y ${last}` : last;
}

function formatRD(price) {
    return Math.round(Number(price) || 0).toLocaleString('en-US');
}

function brandOf(row) {
    const name = (row.nombre || '').toLowerCase();
    if (name.includes('zombie')) return 'Zombie BMX';
    if (name.includes('carolina')) return 'Carolina Princess';
    if (name.includes('sprint')) return 'Julen Sprint';
    if (name.includes('raleigh')) return 'Raleigh';
    if (name.includes('julen')) return 'Julen';
    return 'Raleigh';
}

function isBalance(row) {
    return /balance|walker/i.test(row.nombre || '');
}

function ageOf(row) {
    if (isBalance(row)) return '1.5 a 3 años (sin pedales)';
    return AGES[row.categoria] || '';
}

function ageShort(row) {
    if (isBalance(row)) return '1.5 a 3 años';
    return AGES_SHORT[row.categoria] || '';
}

// 'Bic. 12 BMX Carolina Princess Rosa' -> 'BMX Carolina Princess Rosa'; 'RALEIGH 12 AQUA' -> 'Raleigh 12 Aqua'
function prettyName(row) {
    let name = String(row.nombre || '').replace(/^bic\.?\s*\d+\s*/i, '').replace(/\s+/g, ' ').trim();
    if (name === name.toUpperCase()) {
        name = name.toLowerCase().replace(/(^|\s)(\S)/g, (m, sp, ch) => sp + ch.toUpperCase()).replace(/\bBmx\b/g, 'BMX');
    }
    return name;
}

// Who the bike is for, read from the supplier's name: Girl/Princess, Boy/Zombie, the rest fits both
function genderOf(row) {
    const text = `${row.nombre || ''} ${row.descripcion || ''}`.toLowerCase();
    if (/girl|niña|princess|aura|femenina/.test(text)) return 'nina';
    if (/boy|niño|zombie/.test(text)) return 'nino';
    return 'unisex';
}

function slugify(text) {
    return String(text || '').toLowerCase().trim()
        .replace(/\s+/g, '-').replace(/[^\w-]+/g, '').replace(/-{2,}/g, '-').replace(/^-+/, '').replace(/-+$/, '');
}

// Adds a stable `slug` to each row; two bikes with the same name get their code appended
function withSlugs(rows) {
    const counts = {};
    rows.forEach(r => { const s = slugify(r.nombre); counts[s] = (counts[s] || 0) + 1; });
    return rows.map(r => {
        const base = slugify(r.nombre);
        return { ...r, slug: counts[base] > 1 ? `${base}-${slugify(r.codigo)}` : base };
    });
}

function available(row) {
    return String(row.disponible).toLowerCase() !== 'false';
}

// The supplier's import text names other shops; the owner's rule is that they never show on the site.
// 'Bicicleta Raleigh Aro 12" - Calidad y garantía Bicicentro' -> 'Bicicleta Raleigh Aro 12"'
const SUPPLIER_PHRASES = [/calidad y garant[ií]a\s+(bicicentro|aro\s*(y|&)\s*pedal)/gi, /\bbicicentro\b/gi, /\baro\s*(y|&)\s*pedal\b/gi, /\bla\s+bicicleter[ií]a\b/gi];
function cleanDescription(text) {
    let out = String(text || '');
    SUPPLIER_PHRASES.forEach(re => { out = out.replace(re, ''); });
    return out.replace(/\s*[-–,:;]+\s*$/g, '').replace(/^\s*[-–,:;]+\s*/g, '').replace(/\s{2,}/g, ' ').trim();
}

// A description that only repeats the name ('Bicicleta Raleigh Aro 12"') adds nothing to the page
function extraDescription(row) {
    const text = cleanDescription(row.descripcion);
    if (text.length <= 12 || /^bicicleta\s+\S+\s+aro\s+\d+"?$/i.test(text)) return null;
    return text;
}

// The bikes the catalog shows (same set the shop and the home carousel load): promoted sizes that are
// in stock. Pages, sitemap, feed and the Google profile follow the catalog.
function inCatalog(rows) {
    return withSlugs(rows).filter(r => promoted(r) && available(r));
}

// Wheel sizes present, smallest first: ['12', '16', '20']
function arosOf(rows) {
    return [...new Set(rows.map(r => String(r.categoria)))].sort((a, b) => Number(a) - Number(b));
}

function audience(row) {
    return row.categoria === '29' ? ageShort(row) : `niños de ${ageShort(row)}`;
}

function navidadTitle(row) {
    return `Bicicleta ${prettyName(row)} aro ${row.categoria} para ${audience(row)} | PROOMTB & ROAD`;
}

function navidadDescription(row) {
    return `Bicicleta ${brandOf(row)} aro ${row.categoria} para ${audience(row)}, RD$${formatRD(row.precio)}. Armada y calibrada, con plan de apartado para Navidad y Reyes. Retírala en Los Prados, Santo Domingo.`;
}

function navidadWhatsApp(row) {
    const message = `¡Hola PROO MTB! 🎄 Quiero apartar la bicicleta navideña:
🚲 *${row.nombre}*
📏 Aro: ${row.categoria}"
💰 Precio: RD$ ${formatRD(row.precio)}
🏷️ Código: ${row.codigo || 'N/A'}

¿Cómo puedo coordinar el apartado o entrega en tienda?`;
    return `https://wa.me/18297163555?text=${encodeURIComponent(message)}`;
}

module.exports = { NAVIDAD, AGES, PROMO_AROS, formatRD, brandOf, genderOf, ageOf, ageShort, prettyName, slugify, withSlugs, available, promoted, inCatalog, arosOf, cleanDescription, extraDescription, navidadTitle, navidadDescription, navidadWhatsApp };
